/**
 * Custom Element for Sticky Buy Now Button (sticky-buynow)
 */

class StickyBuynow extends HTMLElement {
    constructor() {
        super();
        this.mainForm = document.querySelector('#main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.priceContainer = this.querySelector('.product__price');
        this.addonsetInfo = this.querySelector('.addonset__info');
        this.mainFormSubmitButton = this.mainForm?.querySelector('button[type="submit"]');
        this.loadingSpinner = this.querySelector('.loading-overlay__spinner');
        this.currentVariant = this.dataset.productVariantId;
        this.sectionId = this.dataset.sectionId;
        this.productUrl = this.dataset.productUrl;

        this.badgeElement = this.priceContainer.querySelector('.price__badge-sale');
        this.discountElement = this.priceContainer.querySelector('.price__discount');
        this.currentBadgeText = this.badgeElement?.innerHTML;
        this.currentDiscountText = this.discountElement?.innerHTML;
        // Initialize observers as class properties
        this.intersectionObserver = null;
        this.buttonStateObserver = null;

        this.observerOptions = {
            root: null,
            rootMargin: "-100px",
            threshold: 0
        };
    }
    
    init() {
        document.addEventListener('variant:changed', this.handleVariantChange.bind(this));
        document.addEventListener('price:updated', this.handlePriceUpdates.bind(this));
        this.setupIntersectionObserver();
        this.setupButtonStateObserver();
    }

    connectedCallback() {
        this.init();
    }

    disconnectedCallback() {
        // Cleanup all observers
        [this.intersectionObserver, this.buttonStateObserver].forEach(observer => {
            observer?.disconnect();
        });

        // Remove event listeners
        document.removeEventListener('variant:changed', this.handleVariantChange);
        document.removeEventListener('price:updated', this.handlePriceUpdates);
    }

    handlePriceUpdates(event) {
        const { variant_id, price, compare_at_price, badge, discount, reset, addons } = event.detail;
        if (reset || event.detail.srcElement) {
            this.resetProductPrice(event.detail.srcElement);
            this.updateAddonsetInfo(addons);
            return;
        }

        const priceElement = this.priceContainer.querySelector('.price');
        const regularPrice = compare_at_price || price;
        const salePrice = compare_at_price ? price : null;

        // Update price classes
        priceElement.classList.toggle('price--on-sale', Boolean(salePrice));
        priceElement.classList.toggle('price--show-discount', Boolean(discount));
        priceElement.classList.toggle('price--show-badge', Boolean(badge));

        this.updatePrices('.price-item--regular', regularPrice);
        this.updatePrices('.price-item--sale', salePrice);

        // Update badge and discount text
        if (this.badgeElement) {
            this.badgeElement.innerHTML = badge?.text || this.currentBadgeText;
        }

        if (this.discountElement) {
            this.discountElement.innerHTML = discount?.text || this.currentDiscountText;
        }

        this.updateAddonsetInfo(addons);
    }

    updateAddonsetInfo(addons) {
        if (addons && addons.length > 0) {
            const addonsetInfoText = addons.length > 0 ? `+${addons.length} ${addons.length > 1 ? 'Items' : 'Item'}` : null;
            this.renderAddonsetInfo(addonsetInfoText);
        }else{
            this.renderAddonsetInfo();
        }
    }

    renderAddonsetInfo(text) {
        this.addonsetInfo.innerHTML = text;
        this.addonsetInfo.classList.toggle('hidden', !text);
    }

    // Update regular and sale prices
    updatePrices = (selector, value) => {
        if (!value) return;
        this.priceContainer.querySelectorAll(selector).forEach(item => {
            item.innerHTML = Shopify.renderPriceText(value);
        });
    };

    handleVariantChange(event) {
        // Uncomment and implement when variant change handling is needed
        const targetVariant = event.target.currentVariant;
        this.productPrice = this.dataset.productPrice = targetVariant.price;
        this.productCompareAtPrice = this.dataset.productCompareAtPrice = targetVariant.compare_at_price;
        this.mainProductVariantId = this.dataset.productVariantId = targetVariant.id;
        this.resetProductPrice();
    }

    setupIntersectionObserver() {
        if (!("IntersectionObserver" in window)) return;

        try {
            this.intersectionObserver = new IntersectionObserver(
                this.handleIntersection.bind(this),
                this.observerOptions
            );
            this.mainForm && this.intersectionObserver.observe(this.mainForm);
        } catch (error) {
            console.error('Failed to setup intersection observer:', error);
        }
    }

    setupButtonStateObserver() {
        if (!this.mainFormSubmitButton) return;

        try {
            this.buttonStateObserver = new MutationObserver(this.handleButtonStateChange.bind(this));

            this.buttonStateObserver.observe(this.mainFormSubmitButton, {
                attributes: true,
                childList: true,
                subtree: true,
                attributeOldValue: true
            });
        } catch (error) {
            console.error('Failed to setup button state observer:', error);
        }
    }

    handleButtonStateChange(mutations) {
        if (!this.submitButton) return;

        mutations.forEach(mutation => {
            if (mutation.type === 'attributes') {
                this.syncButtonAttribute(mutation.attributeName);
            }

            this.syncButtonState();
        });
    }

    syncButtonAttribute(attributeName) {
        const attributeValue = this.mainFormSubmitButton.getAttribute(attributeName);
        if (attributeValue) {
            this.submitButton.setAttribute(attributeName, attributeValue);
        } else {
            this.submitButton.removeAttribute(attributeName);
        }
    }

    syncButtonState() {
        this.submitButton.className = this.mainFormSubmitButton.className;
        this.submitButton.innerHTML = this.mainFormSubmitButton.innerHTML;

        const isLoading = this.mainFormSubmitButton.classList.contains('loading');
        this.loadingSpinner?.classList.toggle('hidden', !isLoading);
    }

    handleIntersection(entries) {
        entries.forEach(entry => {
            const isScrollingDown = entry.boundingClientRect.y < 0;
            const shouldShow = !entry.isIntersecting && isScrollingDown;

            this.classList.toggle('show', shouldShow);
            this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        });
    }

    resetProductPrice(source) {
        source = source ? source : document.getElementById(`price-${this.sectionId}`);
        const destination = this.priceContainer;
        if (source && destination) destination.innerHTML = source.innerHTML;
    }
}

customElements.define('sticky-buynow', StickyBuynow);